import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Send,
  Sparkles,
  Plus,
  Trash2,
  BookOpen,
  MessageSquare,
  ArrowUp,
  Phone,
  Cpu,
  RefreshCw,
  FileText,
  Key,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/** SOPFlow AI Assistant — dedicated page. */
export default function Chat() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [input, setInput] = useState(params.get("q") || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const scroller = useRef(null);

  // WhatsApp Assistant States
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Dynamic Custom Model States
  const [showAiSettings, setShowAiSettings] = useState(false);
  const [aiSettings, setAiSettings] = useState({
    custom_provider: "openai",
    custom_model: "gpt-4o-mini",
    custom_api_key: ""
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch {
      /* ignore */
    }
  };

  const loadSession = async (id) => {
    setMessages([]);
    setSessionMeta(null);
    if (!id) return;
    try {
      const { data } = await api.get(`/chat/sessions/${id}`);
      setSessionMeta({ id: data.id, title: data.title });
      setMessages(data.messages);
    } catch {
      /* ignore */
    }
  };

  const loadAiSettings = async () => {
    try {
      const { data } = await api.get("/connectors/settings");
      setAiSettings({
        custom_provider: data.custom_provider || "openai",
        custom_model: data.custom_model || "gpt-4o-mini",
        custom_api_key: data.custom_api_key || ""
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadSessions();
    loadAiSettings();
  }, []);

  useEffect(() => {
    loadSession(sid);
  }, [sid]);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [messages, busy]);

  const saveAiSettingsInline = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setSettingsSuccess(false);
    try {
      // Fetch full settings first to preserve other flags
      const fullSettings = await api.get("/connectors/settings");
      const updated = {
        ...fullSettings.data,
        custom_provider: aiSettings.custom_provider,
        custom_model: aiSettings.custom_model,
        custom_api_key: aiSettings.custom_api_key
      };
      await api.put("/connectors/settings", updated);
      setSettingsSuccess(true);
      setTimeout(() => {
        setSettingsSuccess(false);
        setShowAiSettings(false);
      }, 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingSettings(false);
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setErr("");
    setInput("");
    const optimistic = [
      ...messages,
      { id: `tmp-${Date.now()}`, role: "user", content: msg, sources: [] },
    ];
    setMessages(optimistic);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/ask", {
        message: msg,
        session_id: sessionMeta?.id || null,
      });
      setMessages([
        ...optimistic,
        {
          id: data.message_id,
          role: "assistant",
          content: data.answer,
          sources: data.sources || [],
        },
      ]);
      if (!sessionMeta) {
        setSessionMeta({ id: data.session_id, title: msg.slice(0, 60) });
        navigate(`/chat/${data.session_id}`, { replace: true });
      }
      loadSessions();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  // Simulate uploading documents via WhatsApp or any other assistant
  const simulateWhatsAppUpload = async (filename, summary, dept) => {
    setUploadingDoc(true);
    setErr("");
    try {
      const { data } = await api.post("/chat/upload-doc", {
        filename,
        content_summary: summary,
        department: dept
      });
      
      const userMsgId = `whatsapp-user-${Date.now()}`;
      const assistantMsgId = `whatsapp-assistant-${Date.now()}`;
      
      const newMessages = [
        ...messages,
        {
          id: userMsgId,
          role: "user",
          content: `📱 WhatsApp assistant: Uploaded document file '${filename}' for SOP extraction.`,
          sources: []
        },
        {
          id: assistantMsgId,
          role: "assistant",
          content: `📥 **WhatsApp Assistant:** Successfully received and parsed document **'${filename}'**!\n\nI have generated a new draft SOP **'${data.title}'** and saved it directly to your ${dept} library. You can view, edit, or submit it for approvals at any time.\n\nSOP ID Ref: *${data.sop_id}*`,
          sources: [{"id": data.sop_id, "title": data.title, "version": "0.1"}]
        }
      ];
      
      setMessages(newMessages);
      loadSessions();
    } catch (e) {
      setErr("Failed to process document through WhatsApp assistant.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const startNew = () => {
    setSessionMeta(null);
    setMessages([]);
    navigate("/chat");
  };

  const removeSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/chat/sessions/${id}`);
    if (sessionMeta?.id === id) startNew();
    loadSessions();
  };

  const suggestions = [
    "How do I receive a warehouse shipment?",
    "What are the steps for handling a security incident?",
    "Write a new SOP about cold storage temperature limits",
    "Create a brief draft SOP for vendor payment process",
  ];

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4 min-h-[calc(100vh-8rem)]" data-testid="chat-page">
      {/* Sidebar of sessions */}
      <GlassCard className="p-3 h-fit lg:sticky lg:top-24" data-testid="chat-sessions">
        <button
          onClick={startNew}
          className="w-full btn-primary-glow rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-1.5"
          data-testid="chat-new-btn"
        >
          <Plus size={14} /> New chat
        </button>
        
        {/* WhatsApp upload document simulator block in sidebar */}
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold px-2 flex items-center gap-1">
            <Phone size={10} /> WhatsApp Assistant
          </div>
          <p className="text-[9px] text-[#9AA4B2] px-2 mt-1 leading-relaxed">Simulate sending documents to our WhatsApp Bot to auto-draft SOPs.</p>
          <div className="mt-2 space-y-1.5 px-1">
            <button
              onClick={() => simulateWhatsAppUpload("Cold_Chain_Protocol.pdf", "Maintains cold-chain log integrity under 4°C with hourly log checks.", "Operations")}
              disabled={uploadingDoc || busy}
              className="w-full text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-xl px-2 py-1.5 text-[10px] text-white flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <FileText size={10} className="text-indigo-400" />
              Upload Cold Chain PDF
            </button>
            <button
              onClick={() => simulateWhatsAppUpload("IT_Password_Policy.docx", "Enforces 14-char multi-factor auth and 90-day key rotations.", "IT")}
              disabled={uploadingDoc || busy}
              className="w-full text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-xl px-2 py-1.5 text-[10px] text-white flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <FileText size={10} className="text-cyan-400" />
              Upload Password Policy DOC
            </button>
          </div>
        </div>

        <div className="mt-5 text-[10px] uppercase tracking-widest text-[#5B6472] px-2">Recent chats</div>
        <div className="mt-1 space-y-1 max-h-[40vh] overflow-y-auto pr-1">
          {sessions.length === 0 && (
            <div className="text-[11px] text-[#5B6472] px-2 py-2">No conversations yet.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/chat/${s.id}`)}
              className={`w-full text-left px-2.5 py-2 rounded-2xl text-sm truncate flex items-center gap-2 group ${
                sessionMeta?.id === s.id
                  ? "bg-white/[0.06] text-white border border-white/10"
                  : "text-[#9AA4B2] hover:text-white hover:bg-white/[0.03]"
              }`}
              data-testid={`session-${s.id}`}
            >
              <MessageSquare size={13} className="shrink-0 opacity-70" />
              <span className="flex-1 truncate">{s.title || "Conversation"}</span>
              <button
                onClick={(e) => removeSession(s.id, e)}
                className="opacity-0 group-hover:opacity-100 transition text-[#5B6472] hover:text-rose-300"
                aria-label="Delete"
              >
                <Trash2 size={12} />
              </button>
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Chat surface */}
      <div className="relative flex flex-col min-h-[70vh]">
        
        {/* Collapsible Model and Key override drawer on Chat page */}
        <GlassCard className="p-3 mb-4 border border-white/15 relative overflow-hidden z-20">
          <button
            type="button"
            onClick={() => setShowAiSettings(prev => !prev)}
            className="w-full flex items-center justify-between text-xs text-[#9AA4B2] hover:text-white transition"
          >
            <span className="flex items-center gap-1.5 font-semibold">
              <Key size={13} className="text-cyan-300" />
              AI Model: <strong className="text-white font-mono">{aiSettings.custom_model}</strong> ({aiSettings.custom_provider})
            </span>
            {showAiSettings ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          
          {showAiSettings && (
            <form onSubmit={saveAiSettingsInline} className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-3 gap-3 text-xs items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#5B6472] mb-1 font-semibold">Provider</label>
                <select
                  value={aiSettings.custom_provider}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, custom_provider: e.target.value }))}
                  className="w-full bg-[#05070D] border border-white/10 rounded-xl p-2 text-white focus:outline-none focus:border-cyan-300"
                >
                  <option value="openai">OpenAI (GPT)</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="google">Google Gemini</option>
                  <option value="custom_host">Private Webhook</option>
                </select>
              </div>
              
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#5B6472] mb-1 font-semibold">Model Name</label>
                <input
                  type="text"
                  value={aiSettings.custom_model}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, custom_model: e.target.value }))}
                  className="w-full bg-[#05070D] border border-white/10 rounded-xl p-2 text-white focus:outline-none focus:border-cyan-300"
                  required
                />
              </div>

              <div className="flex gap-1">
                <input
                  type="password"
                  value={aiSettings.custom_api_key}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, custom_api_key: e.target.value }))}
                  className="flex-1 bg-[#05070D] border border-white/10 rounded-xl p-2 text-white focus:outline-none focus:border-cyan-300 font-mono"
                  placeholder="Private Key (Optional)"
                />
                <Button
                  type="submit"
                  disabled={savingSettings}
                  className="btn-primary-glow rounded-xl px-4 py-2 self-end text-xs font-semibold"
                >
                  {savingSettings ? "Saving..." : "Save"}
                </Button>
              </div>
              
              {settingsSuccess && (
                <div className="sm:col-span-3 text-[11px] text-emerald-400 font-semibold flex items-center gap-1 mt-1">
                  <CheckCircle2 size={12} /> AI Custom Overrides Saved Successfully!
                </div>
              )}
            </form>
          )}
        </GlassCard>

        {messages.length === 0 && !busy && (
          <GlassCard className="p-8 md:p-12 text-center" data-testid="chat-empty">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] items-center justify-center shadow-[0_0_24px_-4px_rgba(79,70,229,0.7)]">
              <Sparkles size={22} className="text-white" />
            </div>
            <h1 className="mt-5 font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
              Ask or Create SOPs via Chat.
            </h1>
            <p className="mt-2 text-[#9AA4B2] max-w-xl mx-auto text-sm md:text-base">
              SOPFlow AI grounded library assistant. Grounded in approved procedures, or instruct me: *"Create a brief draft SOP for warehouse safety"* to auto-draft SOPs instantly!
            </p>
            
            {/* Quick Suggestions */}
            <div className="mt-8 grid sm:grid-cols-2 gap-2.5 max-w-2xl mx-auto text-left">
              {suggestions.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="glass rounded-2xl px-4 py-3 text-sm text-white hover:bg-white/[0.07] transition text-left"
                  data-testid={`suggestion-${q.slice(0, 12)}`}
                  dangerouslySetInnerHTML={{ __html: `<span class='text-cyan-300 mr-2'>→</span> ${q}` }}
                />
              ))}
            </div>

            {/* Premium WhatsApp Assistant document upload simulator block inside empty view */}
            <div className="mt-8 pt-6 border-t border-white/5 max-w-2xl mx-auto text-left">
              <h3 className="text-xs uppercase tracking-widest text-[#5B6472] font-bold mb-3 flex items-center gap-1">
                <Phone size={12} className="text-cyan-300" /> WhatsApp Document Upload Simulator
              </h3>
              <p className="text-xs text-[#9AA4B2] mb-4">
                Simulate your on-field operators snapping or uploading regulatory PDFs to our dedicated WhatsApp Business webhook bot. The assistant instantly parses, analyzes, and drafts structural SOP compliance templates.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => simulateWhatsAppUpload("Cleanroom_Sanitization_Standard.pdf", "Outlines detailed sanitization checklists for Cleanroom Class A including HVAC verification logs.", "Operations")}
                  disabled={uploadingDoc || busy}
                  className="btn-ghost-glass text-xs rounded-full px-4 py-2"
                >
                  <FileText size={13} className="mr-1.5 text-cyan-300" />
                  Upload Cleanroom PDF to WhatsApp Bot
                </Button>
                <Button
                  onClick={() => simulateWhatsAppUpload("Financial_Expense_Policy.txt", "Provides guidelines for mileage travel receipts matching and supervisor approval cycles.", "Finance")}
                  disabled={uploadingDoc || busy}
                  className="btn-ghost-glass text-xs rounded-full px-4 py-2"
                >
                  <FileText size={13} className="mr-1.5 text-indigo-300" />
                  Upload Expense Policy to WhatsApp Bot
                </Button>
              </div>
            </div>

          </GlassCard>
        )}

        {messages.length > 0 && (
          <div
            ref={scroller}
            className="flex-1 space-y-4 overflow-y-auto pr-1 pb-32"
            data-testid="chat-messages"
          >
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} navigate={navigate} userName={user?.name} />
            ))}
            {busy && (
              <div className="flex items-start gap-3">
                <AssistantAvatar />
                <div className="glass rounded-3xl px-4 py-3 flex items-center gap-2 text-sm text-[#9AA4B2]">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" style={{ animationDelay: "0.15s" }} />
                  <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  <span className="ml-1">Consulting your SOP library…</span>
                </div>
              </div>
            )}
            {uploadingDoc && (
              <div className="flex items-start gap-3">
                <AssistantAvatar />
                <div className="glass rounded-3xl px-4 py-3 flex items-center gap-2 text-sm text-cyan-300">
                  <RefreshCw className="animate-spin text-cyan-300" size={14} />
                  <span>WhatsApp Webhook received document. Parsing file content blocks...</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Composer */}
        <div className="mt-4 sticky bottom-0" data-testid="chat-composer">
          {err && (
            <div className="mb-2 text-xs text-rose-300 glass rounded-2xl px-3 py-2" data-testid="chat-error">
              {err}
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="glass-strong rounded-3xl p-2 flex items-end gap-2"
          >
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask, or type: 'Create an SOP about [Title]'..."
              rows={1}
              className="flex-1 bg-transparent border-0 focus-visible:ring-0 text-white placeholder:text-[#5B6472] resize-none min-h-[44px] max-h-40"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chat-send-btn"
              className="btn-primary-glow rounded-full w-11 h-11 p-0 shrink-0"
              aria-label="Send"
            >
              <ArrowUp size={18} />
            </Button>
          </form>
          <div className="mt-2 text-[10px] text-[#5B6472] text-center">
            Grounded in your approved SOPs · GPT-4o-mini · Answers may occasionally be wrong — verify against the cited SOP.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function AssistantAvatar() {
  return (
    <div className="w-9 h-9 shrink-0 rounded-2xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] flex items-center justify-center shadow-[0_0_18px_-4px_rgba(79,70,229,0.7)]">
      <Sparkles size={16} className="text-white" />
    </div>
  );
}

function UserAvatar({ name }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2);
  return (
    <div className="w-9 h-9 shrink-0 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-white text-xs font-medium">
      {initials}
    </div>
  );
}

function MessageBubble({ msg, navigate, userName }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`bubble-${msg.role}`}>
      {isUser ? <UserAvatar name={userName} /> : <AssistantAvatar />}
      <div className={`min-w-0 max-w-[75%] ${isUser ? "text-right" : ""}`}>
        <div
          className={`rounded-3xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-left ${
            isUser
              ? "bg-gradient-to-br from-[#2563EB]/25 via-[#4F46E5]/20 to-[#06B6D4]/15 border border-white/10 text-white"
              : "glass text-white"
          }`}
        >
          {formatWithCitations(msg.content)}
        </div>
        {!isUser && msg.sources?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {msg.sources.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/sops/${s.id}`)}
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-cyan-300 hover:bg-white/[0.08] hover:text-white transition"
                data-testid={`source-${s.id}`}
              >
                <BookOpen size={11} /> {s.title} · v{s.version}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Convert `[SOP: Title v1.0]` mentions to inline glowing chips. */
function formatWithCitations(text) {
  if (!text) return null;
  const re = /\[SOP:\s*([^\]]+)\]/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={m.index}
        className="inline-flex items-center gap-1 px-2 py-0.5 mx-0.5 rounded-full text-[11px] bg-cyan-500/15 border border-cyan-400/30 text-cyan-200"
      >
        <BookOpen size={10} /> {m[1].trim()}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
